import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import PageHeader1 from '../../components/common/PageHeader1';

function WalletDeductionConfig() {
    const BASE_URL = process.env.REACT_APP_BASEURL;
    const getAuthToken = () => localStorage.getItem("astroVani-admin-token");

    const { register, handleSubmit, watch, reset, formState: { errors } } = useForm({
        defaultValues: {
            mode: 'ratio',
            bonusSharePercent: 50,
            rechargeSharePercent: 50
        }
    });

    const currentMode = watch('mode');

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(`${BASE_URL}/admin/walletDeductionConfig`, {
                    headers: {
                        'Authorization': getAuthToken(),
                    },
                });

                if (!response.ok) throw new Error("Failed to fetch wallet deduction config");
                const data = await response.json();

                if (data?.data?.value) {
                    reset(data.data.value);
                }
            } catch (error) {
                console.error("Fetch error:", error);
                toast.error(error.message || "Failed to fetch details");
            }
        };

        fetchConfig();
    }, [BASE_URL, reset]);

    const onSubmit = async (data) => {
        try {
            const payload = {
                mode: data.mode
            };

            if (data.mode === 'ratio') {
                const bonusStr = String(data.bonusSharePercent);
                const rechargeStr = String(data.rechargeSharePercent);
                if (bonusStr.includes('.') || rechargeStr.includes('.') || !/^\d+$/.test(bonusStr) || !/^\d+$/.test(rechargeStr)) {
                    toast.error("Only integer values are allowed (no decimals).");
                    return;
                }
                const bonus = parseInt(data.bonusSharePercent, 10);
                const recharge = parseInt(data.rechargeSharePercent, 10);
                if (bonus + recharge !== 100) {
                    toast.error("Bonus share and Recharge share must add up to 100");
                    return;
                }
                payload.bonusSharePercent = bonus;
                payload.rechargeSharePercent = recharge;
            }

            const response = await fetch(`${BASE_URL}/admin/walletDeductionConfig`, {
                method: 'PATCH',
                headers: {
                    'Authorization': getAuthToken(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (!response.ok || (result && result.success === false) || (result && result.status === 'error')) {
                throw new Error(result.message || "Failed to update wallet deduction config");
            }

            toast.success(result.message || "Wallet deduction config updated successfully");
        } catch (error) {
            console.error("Update error:", error);
            toast.error(error.message || "Failed to update config");
        }
    };

    return (
        <div className="container-xxl">
            <PageHeader1 headerTitle="Wallet Deduction Config" />
            <div className="row g-3 mb-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom-0">
                            <h6 className="m-0 fw-bold">Configure Deduction Priority</h6>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="form-label">Deduction Mode <span className="text-danger">*</span></label>
                                        <select className={`form-select ${errors.mode ? 'is-invalid' : ''}`} {...register('mode', { required: true })}>
                                            <option value="ratio">Ratio (Simultaneous Deduction)</option>
                                            {/* <option value="bonus_first">Bonus First</option>
                                            <option value="recharge_first">Recharge First</option> */}
                                        </select>
                                        {errors.mode && <span className="text-danger">Mode is required</span>}
                                    </div>

                                    {currentMode === 'ratio' && (
                                        <>
                                            <div className="col-md-6">
                                                <label className="form-label">Bonus Share Percent <span className="text-danger">*</span></label>
                                                <input
                                                    type="number"
                                                    step="1"
                                                    onKeyDown={(e) => {
                                                        if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onPaste={(e) => {
                                                        const pasteData = e.clipboardData.getData('text');
                                                        if (pasteData.includes('.') || !/^\d+$/.test(pasteData.trim())) {
                                                            e.preventDefault();
                                                            toast.error("Only whole integer numbers are allowed");
                                                        }
                                                    }}
                                                    className={`form-control ${errors.bonusSharePercent ? 'is-invalid' : ''}`}
                                                    {...register('bonusSharePercent', {
                                                        required: currentMode === 'ratio' ? "Bonus share percent is required" : false,
                                                        min: { value: 0, message: "Minimum percentage is 0" },
                                                        max: { value: 100, message: "Maximum percentage is 100" },
                                                        validate: (val) => {
                                                            if (val === '' || val === null || val === undefined) return true;
                                                            if (String(val).includes('.') || !/^\d+$/.test(String(val))) {
                                                                return "Only integer values are allowed (no decimals)";
                                                            }
                                                            return true;
                                                        }
                                                    })}
                                                    placeholder="e.g. 50"
                                                />
                                                {errors.bonusSharePercent && <span className="text-danger">{errors.bonusSharePercent.message || "Valid integer percentage between 0 and 100 is required"}</span>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Recharge Share Percent <span className="text-danger">*</span></label>
                                                <input
                                                    type="number"
                                                    step="1"
                                                    onKeyDown={(e) => {
                                                        if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onPaste={(e) => {
                                                        const pasteData = e.clipboardData.getData('text');
                                                        if (pasteData.includes('.') || !/^\d+$/.test(pasteData.trim())) {
                                                            e.preventDefault();
                                                            toast.error("Only whole integer numbers are allowed");
                                                        }
                                                    }}
                                                    className={`form-control ${errors.rechargeSharePercent ? 'is-invalid' : ''}`}
                                                    {...register('rechargeSharePercent', {
                                                        required: currentMode === 'ratio' ? "Recharge share percent is required" : false,
                                                        min: { value: 0, message: "Minimum percentage is 0" },
                                                        max: { value: 100, message: "Maximum percentage is 100" },
                                                        validate: (val) => {
                                                            if (val === '' || val === null || val === undefined) return true;
                                                            if (String(val).includes('.') || !/^\d+$/.test(String(val))) {
                                                                return "Only integer values are allowed (no decimals)";
                                                            }
                                                            return true;
                                                        }
                                                    })}
                                                    placeholder="e.g. 50"
                                                />
                                                {errors.rechargeSharePercent && <span className="text-danger">{errors.rechargeSharePercent.message || "Valid integer percentage between 0 and 100 is required"}</span>}
                                            </div>
                                            <div className="col-12 text-muted small">
                                                Note: Bonus Share and Recharge Share must exactly equal 100.
                                            </div>
                                        </>
                                    )}

                                    <div className="col-12 mt-4">
                                        <button type="submit" className="btn btn-primary">Save Config</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WalletDeductionConfig;
